import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

/**
 * Authored bloom parameters. `stormGain`/`thresholdDrop` are applied at full
 * storm (`storm === 1`); the mobile profile keeps the effect visibly cheaper.
 */
const BLOOM = {
  desktop: {
    strength: 0.2,
    radius: 0.45,
    threshold: 0.85,
    stormGain: 1.35,
    thresholdDrop: 0.12,
  },
  mobile: {
    strength: 0.16,
    radius: 0.45,
    threshold: 0.85,
    stormGain: 0.75,
    thresholdDrop: 0.06,
  },
} as const;

/**
 * Authored grade parameters. `stormBoost` scales the grade a little moodier in
 * storms; mobile keeps contrast/split/grain/vignette lower per the spec.
 */
const GRADE = {
  desktop: {
    saturation: 1.06,
    contrast: 1.045,
    split: 0.55,
    // Film grain disabled deliberately (2026-10-12): over the dark storm water it
    // read as sensor noise across the whole frame, which the user rejected. The
    // shader term is kept so a future storm-mood pass can bring it back with a
    // taste decision instead of a rewrite.
    grain: 0.0,
    vignette: 0.22,
    stormBoost: 0.35,
    stormSaturationDrop: 0.12,
    stormContrastBoost: 0.05,
  },
  mobile: {
    saturation: 1.03,
    contrast: 1.02,
    split: 0.35,
    grain: 0.0,
    vignette: 0.15,
    stormBoost: 0.2,
    stormSaturationDrop: 0.08,
    stormContrastBoost: 0.03,
  },
} as const;

/**
 * Colour grade + vignette pass. Runs in the linear-srgb composer buffer (before
 * `OutputPass`), so contrast/saturation are applied to scene-referred values.
 *
 * On top of the existing contrast/brightness/tint/vignette it adds a
 * teal-shadow / warm-highlight split tone and an animated, deterministic film
 * grain (pixel hash — no random state — so screenshot baselines stay stable).
 */
const GradeShader = {
  name: 'GradeVignetteShader',
  uniforms: {
    tDiffuse: { value: null as THREE.Texture | null },
    saturation: { value: 1.06 },
    contrast: { value: 1.04 },
    brightness: { value: 1.0 },
    tint: { value: new THREE.Color(1, 1, 1) },
    vignette: { value: 0.22 },
    vignetteOffset: { value: 1.0 },
    shadowTint: { value: new THREE.Vector3(-0.018, 0.002, 0.03) },
    highlightTint: { value: new THREE.Vector3(0.03, 0.014, -0.01) },
    splitAmount: { value: 0.0 },
    grain: { value: 0.0 },
    grainTime: { value: 0.0 },
    resolution: { value: new THREE.Vector2(1, 1) },
  },
  vertexShader: /* glsl */ `
    varying vec2 vUv;
    void main() {
      vUv = uv;
      gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
  `,
  fragmentShader: /* glsl */ `
    uniform sampler2D tDiffuse;
    uniform float saturation;
    uniform float contrast;
    uniform float brightness;
    uniform vec3 tint;
    uniform float vignette;
    uniform float vignetteOffset;
    uniform vec3 shadowTint;
    uniform vec3 highlightTint;
    uniform float splitAmount;
    uniform float grain;
    uniform float grainTime;
    uniform vec2 resolution;
    varying vec2 vUv;

    // Deterministic 2D hash (no random state, so grain is reproducible).
    float hash21(vec2 p) {
      p = fract(p * vec2(234.34, 435.345));
      p += dot(p, p + 34.23);
      return fract(p.x * p.y);
    }

    void main() {
      vec4 texel = texture2D(tDiffuse, vUv);
      vec3 color = texel.rgb;

      float luma = dot(color, vec3(0.299, 0.587, 0.114));
      color = mix(vec3(luma), color, saturation);
      color = (color - 0.5) * contrast + 0.5;
      color *= brightness;
      color *= tint;

      // Split tone: lift shadows toward teal, highlights toward warm.
      color += shadowTint * ((1.0 - luma) * splitAmount);
      color += highlightTint * (luma * splitAmount);

      // Film grain: animated hash over the backing-buffer pixel position.
      vec2 px = vUv * resolution;
      color += (hash21(px + vec2(grainTime * 61.7, -grainTime * 37.1)) - 0.5) * grain;

      vec2 uv = (vUv - 0.5) * 2.0;
      float vig = max(0.0, vignetteOffset - vignette * dot(uv, uv));
      color *= vig;

      texel.rgb = color;
      gl_FragColor = texel;
    }
  `,
};

/** Uniforms exposed for tuning the colour-grade/vignette pass. */
export type GradeUniforms = typeof GradeShader.uniforms;

/** Film-grain hash advances by a fixed step per rendered frame. */
const GRAIN_TIME_STEP = 1 / 60;

/**
 * Owns the post-processing chain:
 *   RenderPass → UnrealBloomPass → grade/vignette ShaderPass → (SMAA) → OutputPass
 *
 * On desktop the composer render targets are multisampled (MSAA); on mobile a
 * cheaper `SMAAPass` is used instead. The bloom and grade uniforms are exposed
 * so `Game` (or the debug GUI) can tune them live, and the grade/bloom respond
 * to `setStorm` / `setGrade`.
 */
export class PostFxSystem {
  readonly composer: EffectComposer;
  readonly bloom: UnrealBloomPass;
  readonly grade: GradeUniforms;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly mobile: boolean;
  private readonly gradePass: ShaderPass;
  private readonly smaaPass: SMAAPass | null;
  private readonly outputPass: OutputPass;

  /** Grade intensity 0..1 (authored grade is fully on by default). */
  private gradeStrength = 1;
  /** Storm intensity 0..1; emphasises bloom and grade when > 0. */
  private storm = 0;
  /** Animated film-grain clock, advanced once per rendered frame. */
  private grainTime = 0;

  constructor(
    renderer: THREE.WebGLRenderer,
    scene: THREE.Scene,
    camera: THREE.PerspectiveCamera,
    mobile: boolean,
  ) {
    this.renderer = renderer;
    this.mobile = mobile;

    this.composer = this.createComposer(mobile);
    // Normalise the composer buffers to the renderer's current pixel ratio.
    const pixelRatio = renderer.getPixelRatio();
    this.composer.setPixelRatio(pixelRatio);
    this.composer.addPass(new RenderPass(scene, camera));

    const size = renderer.getSize(new THREE.Vector2());
    const bloomBase = mobile ? BLOOM.mobile : BLOOM.desktop;
    this.bloom = new UnrealBloomPass(
      new THREE.Vector2(size.width, size.height),
      bloomBase.strength,
      bloomBase.radius,
      bloomBase.threshold,
    );
    this.composer.addPass(this.bloom);

    this.gradePass = new ShaderPass(GradeShader);
    this.grade = this.gradePass.uniforms as GradeUniforms;
    this.composer.addPass(this.gradePass);
    // Grain hash is pixel-based, so keep it aligned with the backing buffer.
    this.grade.resolution.value.set(size.width * pixelRatio, size.height * pixelRatio);

    this.smaaPass = mobile ? new SMAAPass() : null;
    if (this.smaaPass) this.composer.addPass(this.smaaPass);

    this.outputPass = new OutputPass();
    this.composer.addPass(this.outputPass);

    // Publish the authored defaults (grade on, calm weather) to the uniforms.
    this.applyStorm();
    this.applyGrade();
  }

  private createComposer(mobile: boolean): EffectComposer {
    if (mobile) return new EffectComposer(this.renderer);

    const maxSamples = this.renderer.capabilities.maxSamples ?? 0;
    const samples = maxSamples > 0 ? Math.min(4, maxSamples) : 0;

    if (samples === 0) return new EffectComposer(this.renderer);

    // EffectComposer treats a supplied render target's dimensions as logical
    // pixels and multiplies by `_pixelRatio` internally, so pass logical size
    // here; `setPixelRatio` above resizes to the HiDPI backing size.
    const size = this.renderer.getSize(new THREE.Vector2());
    const target = new THREE.WebGLRenderTarget(
      Math.max(1, Math.floor(size.width)),
      Math.max(1, Math.floor(size.height)),
      { type: THREE.HalfFloatType, samples },
    );
    target.texture.name = 'PostFxSystem.rt1';
    return new EffectComposer(this.renderer, target);
  }

  /**
   * Storm emphasis: strengthens the bloom and lowers its threshold so lightning,
   * spray, foam and muzzle flashes read brightly against the dark sea. Grade
   * gets a small mood boost too.
   */
  setStorm(storm: number): void {
    this.storm = clamp01(storm);
    this.applyStorm();
    this.applyGrade();
  }

  /**
   * Grade intensity 0..1. At 0 the image is neutral; at 1 the authored
   * contrast / split tone / grain / vignette are fully applied. Tunable live.
   */
  setGrade(strength: number): void {
    this.gradeStrength = clamp01(strength);
    this.applyGrade();
  }

  /** Sync the composer buffers with the renderer's current size/pixel ratio. */
  resize(): void {
    const dpr = this.renderer.getPixelRatio();
    const size = this.renderer.getSize(new THREE.Vector2());
    this.composer.setPixelRatio(dpr);
    this.composer.setSize(size.width, size.height);
    // `EffectComposer.setSize` resizes every pass; keep the grain hash aligned
    // with the new backing-buffer dimensions.
    this.grade.resolution.value.set(size.width * dpr, size.height * dpr);
  }

  /** Render the whole chain (replaces `renderer.render(scene, camera)`). */
  render(): void {
    this.grainTime += GRAIN_TIME_STEP;
    this.grade.grainTime.value = this.grainTime;

    // `EffectComposer` resets `renderer.info` at the start of every pass, so
    // the default counters only ever describe the final full-screen quad. Keep
    // them cumulative across the chain (and reset manually per frame) so the
    // canvas inspector reports the real scene draw-call/triangle budget.
    this.renderer.info.reset();
    this.renderer.info.autoReset = false;
    this.composer.render();
    this.renderer.info.autoReset = true;
  }

  dispose(): void {
    this.composer.dispose();
    this.bloom.dispose();
    this.smaaPass?.dispose();
    this.gradePass.material.dispose();
    this.outputPass.material.dispose();
  }

  private applyStorm(): void {
    const b = this.mobile ? BLOOM.mobile : BLOOM.desktop;
    this.bloom.strength = b.strength * (1 + b.stormGain * this.storm);
    this.bloom.threshold = Math.max(0, b.threshold - b.thresholdDrop * this.storm);
  }

  private applyGrade(): void {
    const g = this.mobile ? GRADE.mobile : GRADE.desktop;
    // Storms make the grade moodier on top of the requested strength.
    const intensity = clamp01(this.gradeStrength * (1 + g.stormBoost * this.storm));
    const cold = clamp01(this.storm) * this.gradeStrength;
    this.grade.saturation.value = 1 + (g.saturation - 1) * intensity - g.stormSaturationDrop * cold;
    this.grade.contrast.value = 1 + (g.contrast - 1) * intensity + g.stormContrastBoost * cold;
    this.grade.splitAmount.value = g.split * intensity;
    this.grade.grain.value = g.grain * intensity;
    this.grade.vignette.value = g.vignette * intensity;

    // Cold storm split-tone: shadows dip into steel-blue, highlights cool off.
    this.grade.shadowTint.value.set(
      -0.018 - 0.022 * cold,
      0.002 + 0.003 * cold,
      0.03 + 0.05 * cold,
    );
    this.grade.highlightTint.value.set(
      0.03 - 0.02 * cold,
      0.014 - 0.008 * cold,
      -0.01 + 0.028 * cold,
    );
  }
}
